import React, { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import type { Area, Point } from 'react-easy-crop';
import {
  Box,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from '@chakra-ui/react';

import ModalShell from '../../../../../../components/ui/ModalShell';
import { ff } from '../../../../../../theme';
import { squareCropAreaToJpegFile } from '../../lib/profileCropUtils';

export interface ProfilePhotoCropModalProps {
  isOpen: boolean;
  /** Blob URL pointing at the user's chosen image file. Parent must revoke after close. */
  imageSrc: string | null;
  onClose: () => void;
  /** Called after the user confirms; uploads should happen in parent. */
  onCompleteCroppedImage: (file: File) => void | Promise<void>;
}

const ProfilePhotoCropModal: React.FC<ProfilePhotoCropModalProps> = ({
  isOpen,
  imageSrc,
  onClose,
  onCompleteCroppedImage,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isApplying, setIsApplying] = useState(false);
  const croppedPixelsRef = useRef<Area | null>(null);

  const resetForOpen = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    croppedPixelsRef.current = null;
    setIsApplying(false);
  };

  const handleClose = () => {
    resetForOpen();
    onClose();
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    croppedPixelsRef.current = areaPixels;
  }, []);

  const handleApply = async () => {
    if (!imageSrc) {
      return;
    }
    const pixels = croppedPixelsRef.current;
    if (!pixels) {
      return;
    }
    setIsApplying(true);
    try {
      const file = await squareCropAreaToJpegFile(imageSrc, pixels);
      await Promise.resolve(onCompleteCroppedImage(file));
      resetForOpen();
      onClose();
    } catch {
      /* Upload / save failed — parent toast; keep modal open so user can retry */
    } finally {
      setIsApplying(false);
    }
  };

  const hasImage = Boolean(imageSrc);

  return (
    <ModalShell
      isOpen={isOpen}
      // Esc / X are ignored while the crop is uploading (matches the old closeOnEsc guard).
      onClose={() => {
        if (!isApplying) handleClose();
      }}
      closeOnOverlayClick={!isApplying}
      title="Crop profile photo"
      cancelLabel="Cancel"
      isCancelDisabled={isApplying}
      onCancel={handleClose}
      primary={{
        label: 'Use photo',
        onClick: () => void handleApply(),
        tone: 'lime',
        isLoading: isApplying,
        loadingText: 'Saving',
        isDisabled: !hasImage,
      }}
    >
      <VStack spacing={4} align="stretch">
        <Text fontSize="15px" color={ff.body} lineHeight="1.45">
          Pinch or drag to position your face inside the circle. This is how brands will see you on Local
          Creators.
        </Text>

        {hasImage && imageSrc ? (
          <Box
            position="relative"
            w="100%"
            h={{ base: 'min(320px, 62vw)', sm: '360px' }}
            borderRadius="20px"
            overflow="hidden"
            bg={ff.ink}
          >
            <Cropper
              key={imageSrc}
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
        ) : null}

        {hasImage ? (
          <Box px={1}>
            <FlexRow label="Zoom" />
            <Slider
              aria-label="Zoom crop"
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(v) => setZoom(v)}
            >
              <SliderTrack bg={ff.line} h={1.5} borderRadius="full">
                <SliderFilledTrack bg={ff.ink} borderRadius="full" />
              </SliderTrack>
              <SliderThumb boxSize={6} border="2px solid" borderColor={ff.ink} bg={ff.lime} />
            </Slider>
          </Box>
        ) : null}
      </VStack>
    </ModalShell>
  );
};

const FlexRow: React.FC<{ label: string }> = ({ label }) => (
  <Text textStyle="eyebrow" color={ff.text2} mb={2}>
    {label}
  </Text>
);

export default ProfilePhotoCropModal;
